import {
  FloatingToolbar as LiveblocksFloatingToolbar,
  Toolbar,
  type ToolbarBlockSelectorItem,
} from '@liveblocks/react-tiptap'
import { featureFlags } from '@seen/core/featureFlags'
import {
  IconBracketsContain,
  IconSection,
  IconColumns,
  IconColumnsOff,
  IconListCheck,
  IconSnowflake,
  IconSnowflakeOff,
} from '@tabler/icons-react'
import { getHTMLFromFragment } from '@tiptap/core'
import { type Editor, useEditorState } from '@tiptap/react'
import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus'
import { isEqual } from 'lodash-es'
import { type ReactNode, useCallback, useMemo } from 'react'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import {
  canWrapInSpan,
  conditionalWrapKind,
  ownConditionTarget,
} from '#src/components/rich-text-editor/conditional-display/conditionalWrapKind.ts'
import { canConfigureNodes } from '#src/components/rich-text-editor/conditional-display/NodeConfigExtension.ts'
import { useObservationBulkFreeze } from '#src/components/rich-text-editor/ObservationNode/useObservationBulkFreeze.ts'
import { selectionContent } from '#src/components/rich-text-editor/selectionContent.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'

function useCreateTaskFromSelection(editor: Editor | null): () => void {
  const setOpen = useTaskComposerStore((state) => state.setOpen)
  const setDefaultValues = useTaskComposerStore(
    (state) => state.setDefaultValues
  )
  const setOnTaskCreated = useTaskComposerStore(
    (state) => state.setOnTaskCreated
  )

  return useCallback(() => {
    if (!editor) {
      return
    }

    const { from, to } = editor.state.selection
    const slice = editor.state.doc.slice(from, to)
    const selectedHtml = getHTMLFromFragment(slice.content, editor.schema)

    if (!selectedHtml.trim()) {
      return
    }

    setOnTaskCreated((task) => {
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .insertContentAt(from, {
          type: 'mention',
          attrs: {
            reference: task.reference.reference,
          },
        })
        .run()
    })

    setDefaultValues({
      title: selectedHtml,
    })
    setOpen(true)
  }, [editor, setOpen, setDefaultValues, setOnTaskCreated])
}

export interface FloatingToolbarProps {
  editor: Editor | null
  enableColumns?: boolean
}

const NO_CONTENT = { text: false, observations: 0 }

// Both are BubbleMenu effect deps: a fresh object or function each render
// would dispatch an editor transaction, re-render, and loop. Fixed to the
// viewport and on the body, like the Liveblocks toolbar it replaces.
const MENU_OPTIONS: BubbleMenuProps['options'] = {
  strategy: 'fixed',
  placement: 'top',
  offset: 6,
}
const appendToBody = (): HTMLElement => document.body

// Shown over a selection holding text, or (with structured capture on)
// observation nodes alone, which the stock text-only check would miss.
const shouldShowFor =
  (structuredCapture: boolean): NonNullable<BubbleMenuProps['shouldShow']> =>
  ({ editor, view, element, state }) => {
    const hasFocus = view.hasFocus() || element.contains(document.activeElement)
    if (!hasFocus || !editor.isEditable) {
      return false
    }
    const { text, observations } = selectionContent(state)
    return text || (structuredCapture && observations > 0)
  }

export function FloatingToolbar({
  editor,
  enableColumns = false,
}: FloatingToolbarProps): ReactNode {
  const canSetColumns = enableColumns && editor?.can().setColumns(2)
  const canUnsetColumns = enableColumns && editor?.can().unsetColumns()
  const createTaskFromSelection = useCreateTaskFromSelection(editor)
  // Observation nodes in the selection, frozen or made answerable together.
  const bulkFreeze = useObservationBulkFreeze(editor)
  const structuredCapture = useFeatureFlag(featureFlags.structuredCapture)
  const shouldShow = useMemo(
    () => shouldShowFor(structuredCapture),
    [structuredCapture]
  )
  const content =
    useEditorState({
      editor,
      selector: ({ editor: current }) =>
        current ? selectionContent(current.state) : NO_CONTENT,
      equalityFn: isEqual,
    }) ?? NO_CONTENT
  const wrapKind = editor ? conditionalWrapKind(editor.state) : 'section'
  // Only where a sheet beside the editor can show the item's condition.
  const ownTarget =
    editor && canConfigureNodes(editor)
      ? ownConditionTarget(
          editor.state.doc,
          editor.state.selection.from,
          editor.state.selection.to
        )
      : null
  const canMakeConditional =
    structuredCapture &&
    !!editor?.isEditable &&
    (ownTarget !== null ||
      (wrapKind === 'span'
        ? !!editor?.can().wrapInConditionalSpan()
        : !!editor?.can().wrapInConditionalSection()))
  // A whole line holding just the item can still be hidden as a section.
  const canMakeSection =
    canMakeConditional &&
    ownTarget !== null &&
    wrapKind === 'section' &&
    !!editor?.can().wrapInConditionalSection()
  // A whole line selected wraps as a section; the inline span is offered
  // beside it.
  const canMakeInline =
    canMakeConditional &&
    ownTarget === null &&
    wrapKind === 'section' &&
    !!editor &&
    canWrapInSpan(editor.state) &&
    !!editor.can().wrapInConditionalSpan()
  const showFreeze =
    !!bulkFreeze && (bulkFreeze.freezable > 0 || bulkFreeze.frozen > 0)

  // Add checklist to the default block selector items
  const blockSelectorItems = useCallback(
    (defaultItems: ToolbarBlockSelectorItem[]): ToolbarBlockSelectorItem[] => {
      // Find ordered list index to insert checklist after it
      const orderedListIndex = defaultItems.findIndex(
        (item) => item.name === 'Numbered list'
      )
      const insertIndex =
        orderedListIndex !== -1 ? orderedListIndex + 1 : defaultItems.length

      const checklistItem: ToolbarBlockSelectorItem = {
        name: 'Checklist',
        icon: <IconListCheck className="size-4" />,
        isActive: (e) => e.isActive('taskList'),
        setActive: (e) => e.chain().focus().clearNodes().toggleTaskList().run(),
      }

      return [
        ...defaultItems.slice(0, insertIndex),
        checklistItem,
        ...defaultItems.slice(insertIndex),
      ]
    },
    []
  )

  if (!editor) {
    return null
  }

  // Text formatting, columns and tasks act on text; a selection of
  // observation nodes alone gets only the actions that apply to them.
  // Separators sit between groups, never leading.
  const groups = [
    content.text,
    content.text && enableColumns,
    showFreeze,
    canMakeConditional,
    content.text,
  ]
  const separatorBefore = (index: number): boolean =>
    !!groups[index] && groups.slice(0, index).some(Boolean)

  const sections = (
    <>
      {content.text && (
        <>
          <Toolbar.BlockSelector items={blockSelectorItems} />
          <Toolbar.SectionInline />
        </>
      )}

      {content.text && enableColumns && (
        <>
          {separatorBefore(1) && <Toolbar.Separator />}
          <Toolbar.Button
            name="Add columns"
            icon={<IconColumns className="size-4" />}
            onClick={() => editor.chain().focus().setColumns(2).run()}
            disabled={!canSetColumns}
          />
          {canUnsetColumns && (
            <Toolbar.Button
              name="Remove columns"
              icon={<IconColumnsOff className="size-4" />}
              onClick={() => editor.chain().focus().unsetColumns().run()}
            />
          )}
        </>
      )}

      {showFreeze && (
        <>
          {separatorBefore(2) && <Toolbar.Separator />}
          {bulkFreeze.freezable > 0 && (
            <Toolbar.Button
              name={bulkFreeze.freezeLabel}
              icon={<IconSnowflake className="size-4" />}
              onClick={bulkFreeze.freeze}
            />
          )}
          {bulkFreeze.frozen > 0 && (
            <Toolbar.Button
              name={bulkFreeze.unfreezeLabel}
              icon={<IconSnowflakeOff className="size-4" />}
              onClick={bulkFreeze.unfreeze}
            />
          )}
        </>
      )}

      {canMakeConditional && (
        <>
          {separatorBefore(3) && <Toolbar.Separator />}
          <Toolbar.Button
            name={
              ownTarget !== null
                ? 'Make conditional'
                : wrapKind === 'span'
                  ? 'Make text conditional'
                  : 'Make section conditional'
            }
            icon={
              ownTarget !== null || wrapKind === 'span' ? (
                <IconBracketsContain className="size-4" />
              ) : (
                <IconSection className="size-4" />
              )
            }
            onClick={() =>
              // An item that takes its own condition is given it, never a
              // second one around it.
              ownTarget !== null
                ? editor.commands.openNodeConfig(ownTarget)
                : wrapKind === 'span'
                  ? editor.chain().focus().wrapInConditionalSpan().run()
                  : editor.chain().focus().wrapInConditionalSection().run()
            }
            shortcut="⌘⇧K"
          />
          {canMakeSection && (
            <Toolbar.Button
              name="Make section conditional"
              icon={<IconSection className="size-4" />}
              onClick={() =>
                editor.chain().focus().wrapInConditionalSection().run()
              }
            />
          )}
          {canMakeInline && (
            <Toolbar.Button
              name="Make text conditional"
              icon={<IconBracketsContain className="size-4" />}
              onClick={() =>
                editor.chain().focus().wrapInConditionalSpan().run()
              }
            />
          )}
        </>
      )}

      {content.text && (
        <>
          {separatorBefore(4) && <Toolbar.Separator />}
          <Toolbar.Button
            name="Create task from selection"
            icon={<IconNewTask className="size-4" />}
            onClick={createTaskFromSelection}
            shortcut="⌘⇧O"
          />
        </>
      )}
    </>
  )

  // The Liveblocks toolbar opens over text only; it stays the flag-off
  // toolbar so the BubbleMenu replacement ships behind structured capture.
  if (!structuredCapture) {
    return (
      <LiveblocksFloatingToolbar editor={editor}>
        {sections}
      </LiveblocksFloatingToolbar>
    )
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      appendTo={appendToBody}
      options={MENU_OPTIONS}
      className="z-50"
    >
      <Toolbar
        editor={editor}
        className="lb-portal lb-elevation lb-tiptap-floating-toolbar"
      >
        {sections}
      </Toolbar>
    </BubbleMenu>
  )
}
